/**
 * Add Workspace Modal
 *
 * Lets an existing user create an *additional* workspace and pick its plan:
 *   - Free  → no Stripe; single-user personal workspace (with limits).
 *   - Pro   → Stripe; single-user personal workspace (with limits).
 *   - Team  → Stripe per-seat; the only type that can invite others.
 *
 * A user may own multiple paid workspaces, so this is NOT the same as the
 * first-login PlanPicker (which accepts ToS agreements and checks out the
 * ACTIVE workspace). Here we create the workspace row first to obtain its id,
 * then — for paid plans — start a Stripe checkout that TARGETS that new id so
 * the subscription binds to it rather than the currently-active workspace.
 */

import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useSession } from '@/features/auth/hooks/useSession';
import {
  getSpaceLimits,
  createSpace,
  type CreateSpacePlan,
} from '@/lib/api/endpoints/spaces';
import { HttpError } from '@/lib/http';
import { apiFetch, ApiError } from '@/lib/apiClient';
import { SeatStepper } from '@/features/billing/components/SeatStepper';
import { TEAM_MIN_SEATS } from '@/features/billing/components/billing-constants';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import './AddWorkspaceModal.css';

interface AddWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (spaceId: string) => void;
}

// Native (Capacitor) builds cannot run web Stripe checkout — mirror the guard
// in PlanPicker.startPaid so paid plans are blocked on mobile with a message.
const isNativeMobile = Capacitor.isNativePlatform();

interface CheckoutResponse {
  url?: string;
  sessionId?: string;
}

const PLAN_OPTIONS: Array<{
  plan: CreateSpacePlan;
  title: string;
  blurb: string;
}> = [
  { plan: 'free', title: 'Free', blurb: 'Personal workspace, free for 14 days. No card required.' },
  { plan: 'pro', title: 'Pro', blurb: 'Private personal workspace.' },
  { plan: 'team', title: 'Team', blurb: 'Per-seat. Invite others.' },
];

export function AddWorkspaceModal({ isOpen, onClose, onCreated }: AddWorkspaceModalProps) {
  useEscapeKey(onClose, isOpen);
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);

  const { token } = useSession();
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<CreateSpacePlan>('free');
  const [seats, setSeats] = useState<number>(TEAM_MIN_SEATS);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [limitsLoading, setLimitsLoading] = useState(true);
  const [limitsError, setLimitsError] = useState<string | null>(null);
  // Team billing is release-gated server-side (ENABLE_TEAM_BILLING). Default
  // false so the Team option stays hidden until /payment/config confirms it's on.
  const [teamBillingEnabled, setTeamBillingEnabled] = useState(false);

  // Probe limits when the modal opens. We no longer hard-block on the result
  // (a user may own multiple paid workspaces); the fetch only drives the
  // loading/error UI and confirms the session can reach the API.
  useEffect(() => {
    if (!isOpen || !token) return;

    setLimitsLoading(true);
    setError(null);
    setLimitsError(null);
    setName('');
    setPlan('free');
    setSeats(TEAM_MIN_SEATS);

    getSpaceLimits(token)
      .then(() => setLimitsError(null))
      .catch((err) => {
        console.error('Failed to fetch space limits:', err);
        setLimitsError('Failed to load workspace limits. Please try again.');
      })
      .finally(() => setLimitsLoading(false));

    // Probe whether team billing is enabled; hide Team if disabled or on error.
    apiFetch<{ teamBillingEnabled?: boolean }>('payment/config', { authToken: token })
      .then((cfg) => setTeamBillingEnabled(Boolean(cfg?.teamBillingEnabled)))
      .catch(() => setTeamBillingEnabled(false));
  }, [isOpen, token]);

  const retryLimits = () => {
    if (!token) return;
    setLimitsLoading(true);
    setLimitsError(null);
    getSpaceLimits(token)
      .then(() => setLimitsError(null))
      .catch((err) => {
        console.error('Failed to fetch space limits:', err);
        setLimitsError('Failed to load workspace limits. Please try again.');
      })
      .finally(() => setLimitsLoading(false));
  };

  /**
   * Start a Stripe checkout that binds the subscription to the *newly created*
   * workspaceId (not the active one). The backend now reads `workspaceId` from
   * the body to target the right workspace.
   */
  const startCheckout = async (
    workspaceId: string,
    chosenPlan: 'pro' | 'team',
  ): Promise<void> => {
    if (isNativeMobile) {
      setError('Please finish this purchase on the web — mobile checkout for paid workspaces is coming soon.');
      return;
    }

    // Contract matches POST /payment/create-checkout (payment.router.ts):
    // it reads `quantity` (NOT `seats`) for the per-seat team plan and ignores
    // it for pro, and binds the subscription to `workspaceId`.
    const body: { workspaceId: string; plan: 'pro' | 'team'; billing: 'monthly'; quantity?: number; confirmExposure?: boolean } = {
      workspaceId,
      plan: chosenPlan,
      billing: 'monthly',
    };
    if (chosenPlan === 'team') {
      body.quantity = seats;
      // This modal always creates a brand-new, EMPTY workspace, so there is
      // nothing to expose — auto-confirm so the Team checkout isn't blocked by
      // the TYPE_FLIP_NOT_CONFIRMED exposure gate (#3827).
      body.confirmExposure = true;
    }

    const response = await apiFetch<CheckoutResponse>('payment/create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      authToken: token ?? undefined,
    });

    if (response?.url) {
      window.location.href = response.url;
      return;
    }
    if (response?.sessionId) {
      window.location.href = `https://checkout.stripe.com/pay/${response.sessionId}`;
      return;
    }
    setError('Unable to prepare checkout. Please try again.');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError(null);

    // The name field is optional in the UI, but POST /workspaces hard-requires
    // `name` (OpenAPI `required: [name]` rejects a missing name before the
    // handler runs). Default a blank field client-side so both the Free path and
    // the Pro/Team checkout path proceed instead of 400-ing.
    const trimmedName = name.trim() || 'New Workspace';

    try {
      // Create the workspace first so a paid checkout can bind to its id.
      const space = await createSpace(token, {
        plan,
        name: trimmedName,
        ...(plan === 'team' ? { seats } : {}),
      });

      if (plan === 'free') {
        onCreated?.(space.id);
        onClose();
        return;
      }

      // Pro / Team: redirect to Stripe checkout targeting the new workspace.
      // Native mobile is handled inside startCheckout (shows a message, no nav).
      await startCheckout(space.id, plan);
      // On success the browser navigates away to Stripe; if we reach here it was
      // the native-guard short-circuit, so leave the modal open with the message.
    } catch (err: unknown) {
      console.error('Failed to create workspace:', err);

      // Structured error payloads from either http client (HttpError) or
      // apiClient (ApiError) — surface the server message / upgrade hint.
      if (err instanceof HttpError) {
        const payload = err.payload as Record<string, unknown> | undefined;
        if (typeof payload === 'object' && payload !== null) {
          if (payload.upgradeRequired) {
            setError(String(payload.error || 'Workspace limit reached. Upgrade to create more workspaces.'));
          } else if (payload.error) {
            setError(String(payload.error));
          } else {
            setError(err.message);
          }
        } else {
          setError(err.message);
        }
      } else if (err instanceof ApiError) {
        const payload = err.payload as { error?: string } | undefined;
        setError(payload?.error ?? 'Failed to create workspace. Please try again.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to create workspace. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const ctaLabel = loading
    ? plan === 'free'
      ? 'Creating…'
      : 'Starting checkout…'
    : plan === 'free'
      ? 'Create Workspace'
      : plan === 'team'
        ? `Continue to checkout — ${seats} seats`
        : 'Continue to checkout';

  return (
    <div className="add-workspace-modal-overlay" onMouseDown={handleOverlayMouseDown}
 onClick={handleOverlayClick}>
      <div ref={modalContentRef} className="add-workspace-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-workspace-modal-header">
          <h2>Add Workspace</h2>
          <button className="add-workspace-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="add-workspace-modal-body xv-scroll">
          {limitsLoading ? (
            <div className="add-workspace-loading">
              <p>Loading...</p>
            </div>
          ) : limitsError ? (
            <div className="add-workspace-error-state">
              <p className="add-workspace-error">{limitsError}</p>
              <button className="retry-btn" onClick={retryLimits}>
                Try Again
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="add-workspace-form">
              {/* Plan picker — Free skips Stripe; Pro/Team go through per-workspace checkout.
                  The old !canCreate "limit reached" wall is intentionally relaxed: paid plans
                  let the user add another workspace (a user may own multiple paid workspaces). */}
              <div className="form-field">
                <label>Choose a plan</label>
                <div className="add-workspace-plan-grid">
                  {PLAN_OPTIONS.filter((opt) => opt.plan !== 'team' || teamBillingEnabled).map((opt) => (
                    <button
                      type="button"
                      key={opt.plan}
                      className={`add-workspace-plan-card${plan === opt.plan ? ' selected' : ''}`}
                      onClick={() => {
                        setPlan(opt.plan);
                        setError(null);
                      }}
                      aria-pressed={plan === opt.plan}
                    >
                      <span className="add-workspace-plan-title">{opt.title}</span>
                      <span className="add-workspace-plan-blurb">{opt.blurb}</span>
                    </button>
                  ))}
                </div>
              </div>

              {plan === 'team' && (
                <SeatStepper seats={seats} onChange={setSeats} disabled={loading} />
              )}

              <div className="form-field">
                <label htmlFor="workspaceName">Workspace Name (optional)</label>
                <input
                  id="workspaceName"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError(null);
                  }}
                  placeholder="e.g., Side Project, Personal"
                  maxLength={50}
                />
              </div>

              {error && <p className="add-workspace-error">{error}</p>}

              <div className="add-workspace-actions">
                <button type="button" className="cancel-btn" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="create-btn" disabled={loading}>
                  {ctaLabel}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
